import { documentBody } from '@markbird/shared';

/**
 * Pulling one section out of a document, for `[[Note#Heading]]`, `[[Note^id]]`
 * and their embedded forms.
 *
 * This is line-based rather than AST-based on purpose: the result has to be
 * markdown that can be handed back to the shared render pipeline, and slicing
 * the source keeps it byte-identical to what the user wrote. Round-tripping
 * through an AST would quietly reformat it.
 */

/** A heading found in the source, with the lines it governs. */
interface Heading {
  level: number;
  text: string;
  /** Index of the heading line. */
  line: number;
}

/** Normalize a heading for matching: case- and punctuation-insensitive enough. */
function headingKey(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** ATX headings in the body, ignoring `#` inside fenced code. */
function headings(lines: string[]): Heading[] {
  const out: Heading[] = [];
  let fenced = false;
  lines.forEach((line, i) => {
    const fence = /^\s*(```+|~~~+)/.exec(line);
    if (fence) {
      fenced = !fenced;
      return;
    }
    if (fenced) return;
    const m = /^(#{1,6})\s+(.+?)\s*#*\s*$/.exec(line);
    if (m) out.push({ level: m[1]!.length, text: m[2]!.trim(), line: i });
  });
  return out;
}

/**
 * The lines under a heading: the heading itself through to the next heading of
 * the same or a higher level. Subsections come along, which is what a reader
 * expects from "the Setup section".
 *
 * Returns null when no heading matches, so callers can say so rather than
 * silently embedding the whole document.
 */
export function extractHeadingSection(content: string, heading: string): string | null {
  const lines = documentBody(content).split('\n');
  const all = headings(lines);
  const wanted = headingKey(heading);
  const start = all.find((h) => headingKey(h.text) === wanted);
  if (!start) return null;

  const next = all.find((h) => h.line > start.line && h.level <= start.level);
  return lines
    .slice(start.line, next ? next.line : lines.length)
    .join('\n')
    .trimEnd();
}

/** A block id marker at the end of a line: `some text ^my-id`. */
const BLOCK_ID_RE = /\s\^([\p{L}\p{N}-]+)\s*$/u;

/**
 * The block carrying `^id`.
 *
 * A block is the paragraph, list item or table the marker sits at the end of —
 * so the returned text is the run of non-blank lines ending at the marker. The
 * marker itself is stripped: it is an anchor, not content.
 */
export function extractBlock(content: string, blockId: string): string | null {
  const lines = documentBody(content).split('\n');
  const wanted = blockId.toLowerCase();

  const end = lines.findIndex((line) => {
    const m = BLOCK_ID_RE.exec(line);
    return m ? m[1]!.toLowerCase() === wanted : false;
  });
  if (end === -1) return null;

  let start = end;
  while (start > 0 && lines[start - 1]!.trim() !== '') start--;

  const block = lines.slice(start, end + 1);
  block[block.length - 1] = block[block.length - 1]!.replace(BLOCK_ID_RE, '');
  return block.join('\n').trimEnd();
}

/** Every block id defined in a document, for completion and validation. */
export function blockIds(content: string): string[] {
  const out: string[] = [];
  for (const line of documentBody(content).split('\n')) {
    const m = BLOCK_ID_RE.exec(line);
    if (m?.[1]) out.push(m[1]);
  }
  return [...new Set(out)];
}

/**
 * The markdown a reference points at: a heading section, a block, or the whole
 * body. `null` means the anchor was named but not found — worth telling the
 * reader about rather than papering over.
 */
export function resolveSection(
  content: string,
  ref: { heading?: string | null; block?: string | null },
): string | null {
  if (ref.block) return extractBlock(content, ref.block);
  if (ref.heading) return extractHeadingSection(content, ref.heading);
  return documentBody(content).trim();
}
